import React, { useState, useEffect } from 'react';
import { Button, RichTextEditor } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import FileUpload from '@/components/ui/FileUpload';
import { WebsitePageSection } from '@/types';
import { showToast, toast } from '@/lib/toast';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from '@/assets/icons/UploadIcon';
import { Paragraph } from '@/components/ui/Typography';
import { ContentWithFileFormData, ContentWithFileFormDataSchema } from '@/components/Modals/EditContentsModals/EditContentsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

interface InlineFileEditorProps {
  data: WebsitePageSection;
  onSave: (updatedData: Partial<WebsitePageSection>) => void;
  onCancel: () => void;
}

const InlineFileEditor: React.FC<InlineFileEditorProps> = ({ data, onSave, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileUploadMutation = useFileUpload();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(ContentWithFileFormDataSchema),
    defaultValues: {
      title: data.title ?? '',
      description: data.description ?? '',
      fileUrl: data.fileUrl ?? ''
    },
    mode: 'all'
  });

  useEffect(() => {
    reset({
      title: data.title ?? '',
      description: data.description ?? '',
      fileUrl: data.fileUrl ?? ''
    });
    setPreviewUrl(null);
    setUploadedFile(null);
  }, [data, reset]);

  const handleFileChange = (files: File[] | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setUploadedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setValue('fileUrl', objectUrl, { shouldValidate: true });
    } else {
      setUploadedFile(null);
      setPreviewUrl(null);
      setValue('fileUrl', data.fileUrl ?? '', { shouldValidate: true });
    }
  };

  const onSubmit = async (formData: ContentWithFileFormData) => {
    if (!data.sectionTag) {
      showToast.error('Section tag not found.');
      return;
    }

    const uploadFile = async (file: File) => {
      setIsLoading(true);
      try {
        const response = await fileUploadMutation.mutateAsync({ file });
        return response?.data?.fileUrl ?? '';
      } catch (error) {
        setIsLoading(false);
        console.error('File upload failed:', error);
        toast.error('File upload failed. Please try again.');
        return null;
      }
    };

    // Only upload if there's a new file, otherwise use existing fileUrl
    let fileUrl = formData.fileUrl ?? '';
    if (uploadedFile) {
      const uploadedFileUrl = await uploadFile(uploadedFile);
      if (!uploadedFileUrl) {
        setIsLoading(false);
        return;
      }
      fileUrl = uploadedFileUrl;
    }

    try {
      onSave({
        title: formData.title ?? '',
        description: formData.description ?? '',
        fileUrl: fileUrl,
        contentType: 'WITH_FILE'
      });
      
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setUploadedFile(null);
    } catch (error) {
      setIsLoading(false);
      showToast.error('An unexpected error occurred.');
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setUploadedFile(null);
    onCancel();
  };

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const displayUrl = previewUrl || data.fileUrl;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {/* File Preview - Moved to top */}
        {displayUrl && (
          <div className="relative w-full rounded-lg overflow-hidden border border-gray-200">
            <img
              src={displayUrl}
              alt="Preview"
              className="w-full h-48 object-cover"
            />
          </div>
        )}

         {/* File Upload */}
        <Controller
          name="fileUrl"
          control={control}
          render={({ field }) => (
            <FileUpload
              onChange={(files) => {
                handleFileChange(files);
                field.onChange(files ? URL.createObjectURL(files[0]) : '');
              }}
              label=""
              placeholder=""
              $maxSizeMB={5}
              error={errors.fileUrl?.message}
              multiple={false}
              allowedFileTypes={['image/*']}
              containerClassName="border border-[#E5E5E5] bg-white"
              $dataId="inline-file-upload"
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <UploadIcon stroke="#00764B" />
                <Paragraph className="font-semibold text-primary">
                  Click or drag and drop file to upload
                </Paragraph>
                <Paragraph className="text-sm text-text-secondary">
                  Supported document file is PNG, & JPEG with max size of 5mb
                </Paragraph>
                <button
                  type="button"
                  className="bg-[#ECFEF8] text-[#00764B] hover:bg-[#ECFEF8] hover:text-[#000] px-4 py-2 rounded-md font-semibold"
                >
                  Browse file
                </button>
              </div>
            </FileUpload>
          )}
        />

        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={field.onChange}
              className='mt-4'
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.title?.message}
              $dataId="inline-file-title-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
             <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Page Content</label>
                        <RichTextEditor
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder="Description"
                          disabled={isLoading}
                          extendedToolbar={{
                            textStyle: false,
                            fontSize: false,
                            image: false
                          }}                     
                          toolbarContainerClassName="bg-[var(--inputBgColor)]"
                          contentAreaClassName="bg-[var(--inputBgColor)] min-h-[10rem] text-text-primary whitespace-pre-wrap"
                        />
                        {errors.description?.message && (
                          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                        )}
                      </div>
          )}
        />

       
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={handleCancelClick}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default InlineFileEditor;

